import { useState, useEffect, useRef } from 'react';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { supabase, isPreviewMode } from '../services/supabase';
import { auth } from '../services/auth';
import { logger } from '../services/logger';
import { Profile } from '../types';

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkAuth();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [location]);

  async function checkAuth() {
    if (isPreviewMode()) {
      setIsAuthenticated(true);
      setIsAdmin(true);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);

      if (session) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();

        if (!profileError && profile) {
          const typedProfile = profile as Profile;
          setIsAdmin(typedProfile?.role === 'admin');
        }
      }
    } catch (error) {
      logger.error('Auth check error:', error);
      setIsAuthenticated(false);
      setIsAdmin(false);
    }
  }

  async function handleSignOut() {
    try {
      setShowUserMenu(false);
      await auth.signOut();
    } catch (error) {
      logger.error('Sign out error:', error);
      navigate('/', { replace: true });
    }
  }

  const isLandingPage = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || !isLandingPage
        ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 lg:h-20">
          <div className="flex items-center">
            <Link to={isAuthenticated ? '/app' : '/'} className="flex items-center">
              <Logo />
            </Link>
            <div className="hidden md:flex items-center ml-10 space-x-8">
              <Link
                to="/features"
                className={`text-sm font-medium transition-colors ${
                  isScrolled || !isLandingPage ? 'text-slate-600 hover:text-slate-900' : 'text-slate-700 hover:text-slate-900'
                }`}
              >
                Features
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium transition-colors ${
                  isScrolled || !isLandingPage ? 'text-slate-600 hover:text-slate-900' : 'text-slate-700 hover:text-slate-900'
                }`}
              >
                About
              </Link>
              <Link
                to="/pricing"
                className={`text-sm font-medium transition-colors ${
                  isScrolled || !isLandingPage ? 'text-slate-600 hover:text-slate-900' : 'text-slate-700 hover:text-slate-900'
                }`}
              >
                Pricing
              </Link>
            </div>
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`p-2 rounded-full transition-colors ${
                isScrolled || !isLandingPage ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-700 hover:bg-white/50'
              }`}
            >
              {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {showMobileMenu && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-slate-100">
              <div className="px-4 py-6 space-y-2">
                <Link
                  to="/features"
                  className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Features
                </Link>
                <Link
                  to="/about"
                  className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  About
                </Link>
                <Link
                  to="/pricing"
                  className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Pricing
                </Link>
                <div className="pt-4 border-t border-slate-100 mt-4 space-y-2">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/app"
                        className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Open App
                      </Link>
                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          handleSignOut();
                        }}
                        className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/signin"
                        className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/signup"
                        className="block px-4 py-3 rounded-xl text-base font-semibold bg-slate-900 text-white hover:bg-slate-800 text-center transition-colors"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Get Started Free
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <div className="relative">
                  <button
                    ref={buttonRef}
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="h-10 w-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-500 flex items-center justify-center hover:from-coral-500 hover:to-coral-600 transition-all shadow-md"
                  >
                    <User className="h-5 w-5 text-white" />
                  </button>

                  {showUserMenu && (
                    <div
                      ref={menuRef}
                      className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl py-2 border border-slate-100"
                    >
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Admin Panel
                        </Link>
                      )}
                      <Link
                        to="/app"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        Open App
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        Settings
                      </Link>
                      <div className="border-t border-slate-100 my-2" />
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className={`text-sm font-medium transition-colors ${
                    isScrolled || !isLandingPage ? 'text-slate-600 hover:text-slate-900' : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-md hover:shadow-lg"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
